import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { supervisorService, adminService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

export function SupervisorDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shifts, setShifts] = useState<any[]>([]);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [volunteers, setVolunteers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateBookingId, setLateBookingId] = useState<number | null>(null);
  const [shiftNotes, setShiftNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState<{ [key: number]: string }>({});
  const [showVolunteerNoteModal, setShowVolunteerNoteModal] = useState(false);
  const [volunteerNoteBookingId, setVolunteerNoteBookingId] = useState<number | null>(null);
  const [volunteerNoteContent, setVolunteerNoteContent] = useState('');
  const [showShiftLocation, setShowShiftLocation] = useState(true);
  const [showAllShifts, setShowAllShifts] = useState(false);
  const [selectedShiftTimeLeft, setSelectedShiftTimeLeft] = useState('');
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (user.role !== 'supervisor' && user.role !== 'admin') {
      navigate('/');
      return;
    }

    loadShifts();
  }, [authLoading, user, navigate]);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRes = await adminService.getGlobalSettings();
        setShowShiftLocation(settingsRes.data?.show_shift_location ?? true);
      } catch (err) {
        // Settings load failed, use defaults
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Load shift from URL parameter after shifts are loaded
  useEffect(() => {
    if (shifts.length === 0) return;
    
    const shiftIdParam = searchParams.get('shift');
    if (shiftIdParam) {
      const shiftId = parseInt(shiftIdParam);
      if (!isNaN(shiftId) && shifts.find(s => s.id === shiftId)) {
        setSelectedShiftId(shiftId);
        loadVolunteers(shiftId);
        const shift = shifts.find(s => s.id === shiftId);
        setShiftNotes(shift?.supervisor_notes || '');
        return;
      }
    }

    setSelectedShiftId(null);
    setVolunteers([]);
    setShiftNotes('');
    setShowAllShifts(false);
  }, [shifts, searchParams]);

  const loadShifts = async () => {
    try {
      const res = await supervisorService.getShifts();
      setShifts(res.data);
    } catch (err: any) {
      setError('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const loadVolunteers = async (shiftId: number) => {
    try {
      setLoading(true);
      const res = await supervisorService.getShiftVolunteers(shiftId);
      setVolunteers(res.data);
    } catch (err: any) {
      setError('Failed to load volunteers');
    } finally {
      setLoading(false);
    }
  };

  const selectShift = (shiftId: number | null, shouldConfirm = false) => {
    if (shiftId && shouldConfirm) {
      const shift = shifts.find((item) => item.id === shiftId);
      if (shift && !shift.is_supervisor_for_current_user) {
        const confirmed = window.confirm('You are not the supervisor for this shift. Open it anyway?');
        if (!confirmed) return;
      }
    }

    setSelectedShiftId(shiftId);

    if (shiftId) {
      setSearchParams({ shift: String(shiftId) });
      loadVolunteers(shiftId);
      const shift = shifts.find((s) => s.id === shiftId);
      setShiftNotes(shift?.supervisor_notes || '');
    } else {
      setSearchParams({});
      setVolunteers([]);
      setShiftNotes('');
      setShowAllShifts(false);
    }
  };

  const handleShiftChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const shiftId = parseInt(e.target.value);
    selectShift(shiftId || null, true);
  };

  const handleAttendanceChange = async (bookingId: number, status: string, lateMinutes?: number) => {
    try {
      // Find the current volunteer to check their status
      const volunteer = volunteers.find(v => v.booking_id === bookingId);
      
      // If clicking the same status, toggle it back to pending
      const newStatus = volunteer?.attendance_status === status ? 'pending' : status;

      if (newStatus === 'absent') {
        const name = volunteer?.full_name || volunteer?.name || volunteer?.email || 'this volunteer';
        const confirmed = window.confirm(`Mark ${name} as ABSENT?\n\nThis will send an SMS to the volunteer.`);
        if (!confirmed) return;
      }

      await supervisorService.updateAttendance(bookingId, newStatus, lateMinutes);
      setVolunteers((prev) =>
        prev.map((v) => {
          if (v.booking_id !== bookingId) return v;
          const updated: any = { ...v, attendance_status: newStatus, late_by_minutes: newStatus === 'late' ? lateMinutes : null };
          if (newStatus === 'absent' && !v.absent_sms_sent_at) {
            updated.absent_sms_sent_at = new Date().toISOString();
          }
          return updated;
        })
      );
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update attendance');
    }
  };

  const handleLateClick = (bookingId: number) => {
    // Find volunteer to check if already late
    const volunteer = volunteers.find(v => v.booking_id === bookingId);
    if (volunteer?.attendance_status === 'late') {
      // Already late, toggle back to pending
      handleAttendanceChange(bookingId, 'late');
    } else {
      // Not late yet, show time picker modal
      setLateBookingId(bookingId);
      setShowLateModal(true);
    }
  };

  const handleLateSelect = async (minutes: number) => {
    if (lateBookingId) {
      await handleAttendanceChange(lateBookingId, 'late', minutes);
      setShowLateModal(false);
      setLateBookingId(null);
    }
  };

  const handleVolunteerNotesChange = (bookingId: number, notes: string) => {
    setEditingNotes(prev => ({ ...prev, [bookingId]: notes }));
  };

  const handleSaveVolunteerNotes = async (bookingId: number) => {
    try {
      const notes = volunteerNoteBookingId === bookingId ? volunteerNoteContent : editingNotes[bookingId] || '';
      await supervisorService.updateVolunteerNotes(bookingId, notes);
      setVolunteers((prev) =>
        prev.map((v) => (v.booking_id === bookingId ? { ...v, supervisor_notes: notes } : v))
      );
      const newEditingNotes = { ...editingNotes };
      delete newEditingNotes[bookingId];
      setEditingNotes(newEditingNotes);
      setShowVolunteerNoteModal(false);
      setVolunteerNoteBookingId(null);
      setVolunteerNoteContent('');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save notes');
    }
  };

  const openVolunteerNoteModal = (volunteer: any) => {
    setVolunteerNoteBookingId(volunteer.booking_id);
    setVolunteerNoteContent(volunteer.supervisor_notes || '');
    setShowVolunteerNoteModal(true);
  };

  const closeVolunteerNoteModal = () => {
    setShowVolunteerNoteModal(false);
    setVolunteerNoteBookingId(null);
    setVolunteerNoteContent('');
  };

  const handleSaveVolunteerNoteModal = async () => {
    if (volunteerNoteBookingId === null) return;
    await handleSaveVolunteerNotes(volunteerNoteBookingId);
  };

  const handleSaveShiftNotes = async () => {
    if (!selectedShiftId) return;
    try {
      await supervisorService.updateShiftNotes(selectedShiftId, shiftNotes);
      setShifts(prev => prev.map(s => s.id === selectedShiftId ? { ...s, supervisor_notes: shiftNotes } : s));
      alert('Shift notes saved');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save shift notes');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatTimeLeft = (milliseconds: number) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    }

    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  };

  const selectedShift = shifts.find((s) => s.id === selectedShiftId);

  useEffect(() => {
    if (!selectedShift) {
      setSelectedShiftTimeLeft('');
      return;
    }

    const updateTimeLeft = () => {
      const now = new Date();
      const shiftStart = new Date(selectedShift.start_time);
      const shiftEnd = new Date(selectedShift.end_time);
      const isRunning = shiftStart <= now && shiftEnd > now;

      if (!isRunning) {
        setSelectedShiftTimeLeft('');
        return;
      }

      setSelectedShiftTimeLeft(formatTimeLeft(shiftEnd.getTime() - now.getTime()));
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [selectedShiftId, selectedShift]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user || (user.role !== 'supervisor' && user.role !== 'admin')) return null;
  const orderedShifts = [...shifts].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  const currentShiftIndex = orderedShifts.findIndex((shift) => new Date(shift.start_time) <= currentTime && new Date(shift.end_time) > currentTime);
  const quickJumpCurrentShift = currentShiftIndex >= 0 ? orderedShifts[currentShiftIndex] : null;
  const quickJumpUpcomingShift = quickJumpCurrentShift
    ? orderedShifts[currentShiftIndex + 1] || null
    : orderedShifts.find((shift) => new Date(shift.start_time) > currentTime) || null;
  const hasQuickJumpShift = !!quickJumpCurrentShift || !!quickJumpUpcomingShift;

  const formatShiftQuickLabel = (shift: any) => {
    const startParts = String(shift.start_time || '').substring(0, 16).split('T');
    const endParts = String(shift.end_time || '').substring(0, 16).split('T');
    const isNextDay = startParts[0] && endParts[0] && startParts[0] !== endParts[0];
    return `${shift.title || 'Shift'} · ${startParts[0]} ${startParts[1]} - ${endParts[1]}${isNextDay ? ' (next day)' : ''}`;
  };

  const formatRelativeShiftLabel = (shift: any, kind: 'current' | 'upcoming') => {
    const startTime = new Date(shift.start_time).getTime();
    const endTime = new Date(shift.end_time).getTime();
    const diff = kind === 'current' ? endTime - currentTime.getTime() : startTime - currentTime.getTime();
    const label = formatTimeLeft(Math.abs(diff));

    if (kind === 'current') {
      return diff > 0 ? `Ends in ${label}` : 'Ended';
    }

    if (diff > 0) {
      return `Starts in ${label}`;
    }

    if (currentTime.getTime() < endTime) {
      return `Started ${label} ago`;
    }

    return `Ended ${label} ago`;
  };

  const renderQuickJumpButton = (shift: any, label: string) => (
    <button
      key={shift.id}
      type="button"
      onClick={() => selectShift(shift.id, true)}
      className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
        shift.is_supervisor_for_current_user
          ? 'border-green-200 bg-green-50 hover:bg-green-100'
          : 'border-blue-200 bg-blue-50 hover:bg-blue-100'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={`text-xs font-semibold uppercase tracking-wide ${shift.is_supervisor_for_current_user ? 'text-green-700' : 'text-blue-700'}`}>{label}</p>
        {shift.is_supervisor_for_current_user && (
          <span className="rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Your shift
          </span>
        )}
      </div>
      <p className={`mt-1 text-sm font-medium ${shift.is_supervisor_for_current_user ? 'text-green-950' : 'text-blue-950'}`}>{formatShiftQuickLabel(shift)}</p>
      {showShiftLocation && (
        <p className={`mt-1 text-xs ${shift.is_supervisor_for_current_user ? 'text-green-800' : 'text-blue-800'}`}>
          {shift.location ? `Meeting Point: ${shift.location}` : 'Meeting point TBD'}
        </p>
      )}
    </button>
  );
  const presentCount = volunteers.filter((v) => v.attendance_status === 'present').length;
  const absentCount = volunteers.filter((v) => v.attendance_status === 'absent').length;
  const lateCount = volunteers.filter((v) => v.attendance_status === 'late').length;
  const pendingCount = volunteers.filter((v) => v.attendance_status === 'pending').length;

  return (
    <>
      <Header user={user} onLogout={handleLogout} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-6">Supervisor Dashboard</h1>
        <p className="text-sm text-gray-600 mb-6">
         Find the <a href="https://wiki.connectingworlds.dk/supervisor:start" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Supervisor Guides here</a>. Username: <span className="font-mono">supervisor</span> Password: <span className="font-mono">orangefeeling</span>
        </p>

        {error && <div className="bg-red-100 text-red-800 p-4 rounded mb-4">{error}</div>}

        {!selectedShift ? (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <label className="block text-lg font-semibold mb-3">Select Shift</label>
            {(quickJumpCurrentShift || quickJumpUpcomingShift) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {quickJumpCurrentShift && renderQuickJumpButton(quickJumpCurrentShift, `Current shift · ${formatRelativeShiftLabel(quickJumpCurrentShift, 'current')}`)}
                {quickJumpUpcomingShift && renderQuickJumpButton(quickJumpUpcomingShift, `Upcoming shift · ${formatRelativeShiftLabel(quickJumpUpcomingShift, 'upcoming')}`)}
              </div>
            )}

            <div className="mt-4">
              {hasQuickJumpShift && !showAllShifts ? (
                <button
                  type="button"
                  onClick={() => setShowAllShifts(true)}
                  className="inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                >
                  View all shifts
                </button>
              ) : (
                <select
                  value={selectedShiftId || ''}
                  onChange={handleShiftChange}
                  className="w-full border rounded-lg px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Select a shift --</option>
                  {shifts.map((shift) => {
                    // Times from API are already in configured timezone
                    const startParts = shift.start_time.substring(0, 16).split('T');
                    const endParts = shift.end_time.substring(0, 16).split('T');
                    const dateLabel = startParts[0];
                    const startTime = startParts[1];
                    const endTime = endParts[1];
                    const isNextDay = startParts[0] !== endParts[0];

                    return (
                      <option key={shift.id} value={shift.id}>
                        {dateLabel} · {shift.title ? `${shift.title} · ` : ''}{startTime} - {endTime}
                        {isNextDay && ' (next day)'}
                        {showShiftLocation && (shift.location ? ` · Meeting Point: ${shift.location}` : ' · Meeting point TBD')}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 mb-1">Selected shift</p>
                {selectedShift.is_supervisor_for_current_user && (
                  <span className="mb-2 inline-flex rounded-full bg-green-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    Your shift
                  </span>
                )}
                <h2 className="text-2xl font-bold text-gray-900">{selectedShift.title || 'Shift'}</h2>
                <p className="mt-2 text-base font-semibold text-gray-800">
                  {String(selectedShift.start_time || '').substring(0, 16).replace('T', ' ')} - {String(selectedShift.end_time || '').substring(0, 16).replace('T', ' ')}
                </p>
                {selectedShiftTimeLeft && (
                  <p className="mt-2 inline-flex rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-900">
                    Ends in {selectedShiftTimeLeft}
                  </p>
                )}
                {showShiftLocation && (
                  <p className="mt-1 text-sm text-gray-700">
                    {selectedShift.location ? `Meeting Point: ${selectedShift.location}` : 'Meeting point TBD'}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => selectShift(null)}
                className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
              >
                Select different shift
              </button>
            </div>
          </div>
        )}

        {selectedShift && (
          <>
            {selectedShift.supervisor_notes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-yellow-900 mb-2">📝 Admin Notes:</h3>
                <p className="text-yellow-800 whitespace-pre-wrap">{selectedShift.supervisor_notes}</p>
              </div>
            )}

            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Attendance Summary</h2>
                <span className="text-sm text-gray-600">
                  {volunteers.length} volunteer(s) assigned
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-700">{presentCount}</p>
                  <p className="text-sm text-green-600">Present</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-700">{lateCount}</p>
                  <p className="text-sm text-yellow-600">Late</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-700">{absentCount}</p>
                  <p className="text-sm text-red-600">Absent</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-gray-700">{pendingCount}</p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Volunteers</h2>

              {volunteers.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No volunteers assigned to this shift</p>
              ) : (
                <div className="space-y-4">
                  {['volunteer', 'supervisor', 'admin'].map((roleType) => {
                    const volunteersOfRole = volunteers.filter((v: any) => v.role === roleType);
                    if (volunteersOfRole.length === 0) return null;
                    
                    const roleColor = roleType === 'volunteer' ? 'bg-orange-50 border-orange-200' :
                                     roleType === 'supervisor' ? 'bg-yellow-50 border-yellow-200' :
                                     'bg-blue-50 border-blue-200';
                    const roleTextColor = roleType === 'volunteer' ? 'text-orange-700' :
                                         roleType === 'supervisor' ? 'text-yellow-700' :
                                         'text-blue-700';
                    
                    return (
                      <div key={roleType}>
                        <h3 className={`text-sm font-semibold uppercase mb-3 ${roleTextColor}`}>
                          {roleType}s ({volunteersOfRole.length})
                        </h3>
                        <div className="space-y-3">
                          {volunteersOfRole.map((volunteer) => (
                            <div
                              key={volunteer.booking_id}
                              className={`border rounded-lg p-4 ${roleColor}`}
                            >
                              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div className="lg:flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-gray-900">
                                      {volunteer.full_name || volunteer.name || volunteer.email}
                                    </p>
                                    {volunteer.is_first_shift && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 border border-blue-300 rounded text-xs font-medium text-blue-800">
                                        ⭐ First Shift
                                      </span>
                                    )}
                                    {volunteer.sick_note_id && volunteer.sick_note_status === 'pending' && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 border border-orange-300 rounded text-xs font-medium text-orange-800">
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        Pending Sick Note
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">{volunteer.email}</p>
                                  {volunteer.phone && (
                                    <p className="text-sm text-gray-600">📞 {volunteer.phone}</p>
                                  )}
                                  {volunteer.attendance_status === 'late' && volunteer.late_by_minutes && (
                                    <p className="text-sm text-yellow-700 font-medium mt-1">
                                      Late by {volunteer.late_by_minutes} minutes
                                    </p>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:w-auto lg:flex-none">
                                  <button
                                    onClick={() => handleAttendanceChange(volunteer.booking_id, 'present')}
                                    className={`w-full px-4 py-2 rounded font-medium ${
                                      volunteer.attendance_status === 'present'
                                        ? 'bg-green-600 text-white'
                                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                                    }`}
                                  >
                                    Present
                                  </button>
                                  <button
                                    onClick={() => handleLateClick(volunteer.booking_id)}
                                    className={`w-full px-4 py-2 rounded font-medium ${
                                      volunteer.attendance_status === 'late'
                                        ? 'bg-yellow-600 text-white'
                                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                    }`}
                                  >
                                    Late
                                  </button>
                                  <div className="flex flex-col items-center gap-1">
                                    <button
                                      onClick={() => handleAttendanceChange(volunteer.booking_id, 'absent')}
                                      className={`w-full px-4 py-2 rounded font-medium ${
                                        volunteer.attendance_status === 'absent'
                                          ? 'bg-red-600 text-white'
                                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                                      }`}
                                    >
                                      Absent
                                    </button>
                                    {volunteer.absent_sms_sent_at && (
                                      <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                                        SMS sent
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => openVolunteerNoteModal(volunteer)}
                                    className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 font-medium text-sm"
                                  >
                                    📝 Note
                                  </button>
                                </div>
                              </div>
                            </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Shift Notes</h2>
              <p className="text-sm text-gray-600 mb-3">Add notes about this shift that will be visible to admins</p>
              <textarea
                value={shiftNotes}
                onChange={(e) => setShiftNotes(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter shift notes..."
              />
              <button
                onClick={handleSaveShiftNotes}
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
              >
                Save Shift Notes
              </button>
            </div>
          </>
        )}

        {showLateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold mb-4">How late was the volunteer?</h3>
              <p className="text-sm text-gray-600 mb-4">Select the amount of time the volunteer was late</p>
              
              <div className="space-y-2">
                <button
                  onClick={() => handleLateSelect(15)}
                  className="w-full px-4 py-3 bg-yellow-100 hover:bg-yellow-200 text-yellow-900 rounded-lg font-medium text-left"
                >
                  15 minutes
                </button>
                <button
                  onClick={() => handleLateSelect(30)}
                  className="w-full px-4 py-3 bg-yellow-100 hover:bg-yellow-200 text-yellow-900 rounded-lg font-medium text-left"
                >
                  30 minutes
                </button>
                <button
                  onClick={() => handleLateSelect(60)}
                  className="w-full px-4 py-3 bg-yellow-100 hover:bg-yellow-200 text-yellow-900 rounded-lg font-medium text-left"
                >
                  1 hour
                </button>
                <button
                  onClick={() => handleLateSelect(120)}
                  className="w-full px-4 py-3 bg-orange-100 hover:bg-orange-200 text-orange-900 rounded-lg font-medium text-left"
                >
                  2 hours
                </button>
                <button
                  onClick={() => handleLateSelect(180)}
                  className="w-full px-4 py-3 bg-orange-100 hover:bg-orange-200 text-orange-900 rounded-lg font-medium text-left"
                >
                  3+ hours
                </button>
              </div>

              <button
                onClick={() => {
                  setShowLateModal(false);
                  setLateBookingId(null);
                }}
                className="w-full mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showVolunteerNoteModal && volunteerNoteBookingId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold mb-4">Add Note</h3>
              <p className="text-sm text-gray-600 mb-4">Add notes about this volunteer</p>
              
              <textarea
                value={volunteerNoteContent}
                onChange={(e) => setVolunteerNoteContent(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter notes..."
              />

              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSaveVolunteerNoteModal}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                >
                  Save
                </button>
                <button
                  onClick={closeVolunteerNoteModal}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
